import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStorefrontSettings } from "../../../../hooks/StorefrontSettingsContext";
import Api from "../../../../services/Api";
import Notifications from "../../../../services/Notifications";

const fetchDomesticCountries = async () => {
  try {
    const response = await Api.fetch(`/api/Administrative/domesticCountries`);

    if (response && response.domesticCountries) {
      return {
        addressType: response.addressType,
        domesticCountries: response.domesticCountries.map((country) => ({
          id: country.id,
          countryCode: country.countryCode,
          displayName: country.displayName,
          createdAt: country.createdAt,
          updatedAt: country.updatedAt,
        })),
      };
    }

    return {
      addressType: null,
      domesticCountries: [],
    };
  } catch (error) {
    throw error;
  }
};

const updateDomesticCountryRequest = async (request) => {
  try {
    const response = await Api.fetch(
      "/api/Administrative/updateDomesticCountry",
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );

    if (response) {
      return response.json();
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const useDomesticCountries = (localized) => {
  const storeData = useStorefrontSettings();
  const queryClient = useQueryClient();

  const responseMessages = {
    GenericFailure: localized.OperationFailed,
    DuplicateKeyViolation: localized.DomesticCountriesDuplicateKeyViolation,
    DomesticCountryNotFound: localized.DomesticCountriesNotFound,
    StorefrontNotFound: localized.DomesticCountriesStorefrontNotFound,
    Unauthorized: localized.DomesticCountriesUnauthorized,
    InvalidOperation: localized.DomesticCountriesInvalidOperation,
    CannotDeleteLastCountry: localized.DomesticCountriesCannotDeleteLastCountry || "Cannot delete the last domestic country while export processing is enabled. Please disable export processing first or add another domestic country.",
    DeleteSuccess: localized.DomesticCountriesSuccessfulDelete,
    UpdateSuccess: localized.DomesticCountriesSuccessfulUpdate,
    AddSuccess: localized.DomesticCountriesSuccessfulAdd,
  };

  const {
    data: domesticCountries = [],
    isLoading: fetching,
    error,
    refetch: getDomesticCountries,
  } = useQuery({
    queryKey: ["domesticCountries"],
    queryFn: fetchDomesticCountries,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const buildUpdateRequest = (form, operationType) => {
    return {
      id: form.id ?? null,
      storefrontNumber: storeData.storefrontNumber,
      countryCode: form.countryCode,
      displayName: form.displayName,
      operationType: operationType,
    };
  };

  const handleResponseNotification = (result) => {
    const { response, isSuccess, errorMessage } = result;
    if (isSuccess) {
      const message = responseMessages[response];
      if (message) {
        Notifications.message(message);
      }

      return true;
    } else {
      console.error(errorMessage);
      return false;
    }
  };

  const updateDomesticCountryMutation = useMutation({
    mutationFn: async ({ form, operationType }) => {
      const request = buildUpdateRequest(form, operationType);

      return await updateDomesticCountryRequest(request);
    },
    onSuccess: (response) => {
      const isSuccess = handleResponseNotification(response);
      if (isSuccess) {
        queryClient.invalidateQueries({ queryKey: ["domesticCountries"] });
      }

      return { isSuccess, response: response?.response };
    },
    onError: (error) => {
      console.error("Error updating domestic country:", error);

      return { isSuccess: false, response: "GenericFailure" };
    },
  });

  const updateDomesticCountry = async (form, operationType) => {
    const result = await updateDomesticCountryMutation.mutateAsync({
      form,
      operationType,
    });

    return result;
  };

  return {
    domesticCountries: domesticCountries.domesticCountries || [],
    fetching,
    addressType: domesticCountries.addressType,
    getDomesticCountries,
    updateDomesticCountry,
    responseMessages,
    error,
  };
};
